import React, { useEffect, useState, Children } from 'react';
import { motion, AnimatePresence } from 'framer-motion';
import { HeadphonesIcon, GlobeIcon, MessageCircleIcon, TrendingUpIcon, LanguagesIcon, WrenchIcon, BrainIcon, ShieldCheckIcon, ClockIcon, SparklesIcon } from 'lucide-react';
import { ApplicationForm } from '../components/ApplicationForm';
import { ImpressumModal, DatenschutzModal } from '../components/LegalModals';
const fadeInUp = {
  hidden: {
    opacity: 0,
    y: 30
  },
  visible: {
    opacity: 1,
    y: 0
  }
};
const staggerContainer = {
  hidden: {
    opacity: 0
  },
  visible: {
    opacity: 1,
    transition: {
      staggerChildren: 0.1
    }
  }
};
function Header() {
  const [scrolled, setScrolled] = useState(false);
  const [formOpen, setFormOpen] = useState(false);
  useEffect(() => {
    const handleScroll = () => {
      setScrolled(window.scrollY > 50);
    };
    window.addEventListener('scroll', handleScroll);
    return () => window.removeEventListener('scroll', handleScroll);
  }, []);
  return <>
      <motion.header className={`fixed top-0 left-0 right-0 z-50 transition-all duration-300 ${scrolled ? 'bg-slate-900/95 backdrop-blur-md shadow-lg' : 'bg-transparent'}`} initial={{
      y: -100
    }} animate={{
      y: 0
    }} transition={{
      duration: 0.5
    }} data-id="element-400">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8" data-id="element-401">
          <div className="flex items-center justify-between h-16 sm:h-20" data-id="element-402">
            <a href="/" className="text-white font-bold text-xl sm:text-2xl tracking-tight" data-id="element-403">
              CR Careers
            </a>
            <motion.button onClick={() => setFormOpen(true)} className={`px-4 sm:px-6 py-2 sm:py-2.5 rounded-full font-semibold text-sm sm:text-base transition-all duration-300 whitespace-nowrap ${scrolled ? 'bg-orange-500 text-white hover:bg-orange-600' : 'bg-white/10 text-white hover:bg-white/20 border border-white/20'}`} whileHover={{
            scale: 1.02
          }} whileTap={{
            scale: 0.98
          }} data-id="element-404">
              Apply Now
            </motion.button>
          </div>
        </div>
      </motion.header>
      <AnimatePresence data-id="element-405">
        {formOpen && <ApplicationForm isOpen={formOpen} onClose={() => setFormOpen(false)} position="technical-assistant" data-id="element-406" />}
      </AnimatePresence>
    </>;
}
function HeroSection() {
  const [formOpen, setFormOpen] = useState(false);
  return <>
      <section className="relative min-h-screen flex items-center justify-center bg-gradient-to-b from-slate-900 via-slate-900 to-slate-800 overflow-hidden" data-id="element-407">
        <div className="absolute inset-0 overflow-hidden" data-id="element-408">
          <div className="absolute top-1/4 left-1/4 w-96 h-96 bg-orange-500/10 rounded-full blur-3xl" data-id="element-409" />
          <div className="absolute bottom-1/4 right-1/4 w-96 h-96 bg-blue-500/10 rounded-full blur-3xl" data-id="element-410" />
        </div>

        <div className="relative z-10 w-full max-w-5xl mx-auto px-4 sm:px-6 lg:px-8 text-center pt-20" data-id="element-411">
          <motion.div initial="hidden" animate="visible" variants={staggerContainer} className="space-y-6 sm:space-y-8" data-id="element-412">
            <motion.div variants={fadeInUp} className="inline-flex items-center gap-2 px-4 py-2 rounded-full bg-orange-500/10 border border-orange-500/20 text-orange-400 text-sm font-medium" data-id="element-413">
              <SparklesIcon className="w-4 h-4" data-id="element-414" />
              We're Hiring
            </motion.div>

            <motion.h1 variants={fadeInUp} className="text-4xl sm:text-5xl md:text-7xl font-extrabold text-white leading-tight px-4" data-id="element-415">
              Technical Assistant
              <span className="block text-transparent bg-clip-text bg-gradient-to-r from-orange-400 to-orange-600" data-id="element-416">
                Application
              </span>
            </motion.h1>

            <motion.p variants={fadeInUp} className="text-lg sm:text-xl text-slate-400 max-w-2xl mx-auto px-4" data-id="element-417">
              Start the application or find out more about CR Careers
            </motion.p>

            <motion.div variants={fadeInUp} className="flex flex-col sm:flex-row items-center justify-center gap-4 pt-4 px-4" data-id="element-418">
              <motion.button onClick={() => setFormOpen(true)} className="w-full sm:w-auto px-8 py-4 bg-gradient-to-r from-orange-500 to-orange-600 text-white font-semibold rounded-full text-lg shadow-lg shadow-orange-500/25 hover:shadow-orange-500/40 transition-all duration-300" whileHover={{
              scale: 1.02,
              y: -2
            }} whileTap={{
              scale: 0.98
            }} data-id="element-419">
                Start Application
              </motion.button>
              <motion.a href="#about" className="w-full sm:w-auto px-8 py-4 bg-white/5 text-white font-semibold rounded-full text-lg border border-white/10 hover:bg-white/10 transition-all duration-300 text-center" whileHover={{
              scale: 1.02
            }} whileTap={{
              scale: 0.98
            }} data-id="element-420">
                Learn More
              </motion.a>
            </motion.div>
          </motion.div>
        </div>
      </section>
      <AnimatePresence data-id="element-421">
        {formOpen && <ApplicationForm isOpen={formOpen} onClose={() => setFormOpen(false)} position="technical-assistant" data-id="element-422" />}
      </AnimatePresence>
    </>;
}
function AboutSection() {
  const [formOpen, setFormOpen] = useState(false);
  return <>
      <section id="about" className="py-20 sm:py-32 bg-slate-800" data-id="element-423">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8" data-id="element-424">
          <div className="grid lg:grid-cols-2 gap-12 lg:gap-20 items-center" data-id="element-425">
            <motion.div initial="hidden" whileInView="visible" viewport={{
            once: true,
            margin: '-100px'
          }} variants={staggerContainer} className="space-y-6" data-id="element-426">
              <motion.h2 variants={fadeInUp} className="text-3xl sm:text-4xl lg:text-5xl font-bold text-white leading-tight" data-id="element-427">
                What is CR Careers and what do we do?
              </motion.h2>

              <motion.p variants={fadeInUp} className="text-slate-400 text-lg leading-relaxed" data-id="element-428">
                As a distinguished CRM agency, we proudly cater to a diverse
                clientele spanning the domains of coaching, consulting, and
                agency services. Our current robust growth trajectory not only
                reflects our success but also emphasizes the imperative of
                strategic support to fortify and elevate our operations.
              </motion.p>

              <motion.p variants={fadeInUp} className="text-slate-400 text-lg leading-relaxed" data-id="element-429">
                As a Technical Assistant, you'll be at the heart of our
                operations, providing essential technical support to our clients
                and internal teams. You'll help solve technical challenges,
                assist with CRM systems, and ensure smooth operations across all
                our client engagements.
              </motion.p>

              <motion.p variants={fadeInUp} className="text-slate-400 text-lg leading-relaxed" data-id="element-430">
                We're seeking dedicated individuals who are passionate about
                technology, customer service, and problem-solving. Join us in
                supporting coaches and consultants as they scale their
                businesses to six-figure monthly revenues.
              </motion.p>

              <motion.div variants={fadeInUp} data-id="element-431">
                <motion.button onClick={() => setFormOpen(true)} className="inline-flex px-8 py-4 bg-gradient-to-r from-orange-500 to-orange-600 text-white font-semibold rounded-full text-lg shadow-lg shadow-orange-500/25 hover:shadow-orange-500/40 transition-all duration-300" whileHover={{
                scale: 1.02,
                y: -2
              }} whileTap={{
                scale: 0.98
              }} data-id="element-432">
                  Start Application
                </motion.button>
              </motion.div>
            </motion.div>

            <motion.div initial={{
            opacity: 0,
            x: 50
          }} whileInView={{
            opacity: 1,
            x: 0
          }} viewport={{
            once: true,
            margin: '-100px'
          }} transition={{
            duration: 0.6
          }} className="relative" data-id="element-433">
              <div className="relative rounded-2xl overflow-hidden shadow-2xl" data-id="element-434">
                <img src="https://cdn.magicpatterns.com/uploads/fPAHGqxAmZ2UasLoNSHy8B/65a103cc088b375c2ff9e15f_DSC_0644-p-1600_(1).jpg" alt="Professional team member at CR Careers" className="w-full h-auto object-cover" data-id="element-435" />
                <div className="absolute inset-0 bg-gradient-to-t from-slate-900/50 to-transparent" data-id="element-436" />
              </div>
              <div className="absolute -top-4 -right-4 w-24 h-24 bg-orange-500/20 rounded-full blur-2xl" data-id="element-437" />
              <div className="absolute -bottom-4 -left-4 w-32 h-32 bg-blue-500/20 rounded-full blur-2xl" data-id="element-438" />
            </motion.div>
          </div>
        </div>
      </section>
      <AnimatePresence data-id="element-439">
        {formOpen && <ApplicationForm isOpen={formOpen} onClose={() => setFormOpen(false)} position="technical-assistant" data-id="element-440" />}
      </AnimatePresence>
    </>;
}
function BenefitsSection() {
  const [formOpen, setFormOpen] = useState(false);
  const benefits = [{
    icon: HeadphonesIcon,
    title: 'Customer-Focused Environment',
    description: 'Work in a supportive environment where helping clients succeed is at the core of everything we do. Develop strong customer service skills while making a real impact.',
    color: 'orange'
  }, {
    icon: GlobeIcon,
    title: 'Boundless Flexibility',
    description: 'Enjoy the freedom of 100% remote work setup. Work from anywhere in the world, empowering you to strike the perfect balance between personal and professional life.',
    color: 'blue'
  }, {
    icon: MessageCircleIcon,
    title: 'Communication Excellence',
    description: 'Develop and hone your communication and problem-solving skills in an atmosphere that thrives on collaboration and technical excellence.',
    color: 'emerald'
  }, {
    icon: TrendingUpIcon,
    title: 'Career Growth Path',
    description: 'Unlock a pathway to professional advancement. Start as a Technical Assistant and grow into more senior technical or customer success roles as you develop your skills.',
    color: 'purple'
  }];
  const colorClasses: Record<string, {
    bg: string;
    icon: string;
    border: string;
  }> = {
    orange: {
      bg: 'bg-orange-500/10',
      icon: 'text-orange-400',
      border: 'border-orange-500/20'
    },
    blue: {
      bg: 'bg-blue-500/10',
      icon: 'text-blue-400',
      border: 'border-blue-500/20'
    },
    emerald: {
      bg: 'bg-emerald-500/10',
      icon: 'text-emerald-400',
      border: 'border-emerald-500/20'
    },
    purple: {
      bg: 'bg-purple-500/10',
      icon: 'text-purple-400',
      border: 'border-purple-500/20'
    }
  };
  return <>
      <section className="py-20 sm:py-32 bg-slate-900" data-id="element-441">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8" data-id="element-442">
          <motion.div initial="hidden" whileInView="visible" viewport={{
          once: true,
          margin: '-100px'
        }} variants={staggerContainer} className="text-center mb-16" data-id="element-443">
            <motion.h2 variants={fadeInUp} className="text-3xl sm:text-4xl lg:text-5xl font-bold text-white mb-4" data-id="element-444">
              Why choose a career with CR Careers?
            </motion.h2>
            <motion.p variants={fadeInUp} className="text-slate-400 text-lg max-w-2xl mx-auto" data-id="element-445">
              Discover the benefits of joining our growing team
            </motion.p>
          </motion.div>

          <motion.div initial="hidden" whileInView="visible" viewport={{
          once: true,
          margin: '-100px'
        }} variants={staggerContainer} className="grid md:grid-cols-2 gap-6 lg:gap-8" data-id="element-446">
            {benefits.map((benefit, index) => {
            const colors = colorClasses[benefit.color];
            return <motion.div key={index} variants={fadeInUp} className={`p-6 sm:p-8 rounded-2xl bg-slate-800/50 border ${colors.border} backdrop-blur-sm`} data-id="element-447">
                  <div className={`inline-flex p-3 rounded-xl ${colors.bg} mb-4`} data-id="element-448">
                    <benefit.icon className={`w-6 h-6 ${colors.icon}`} data-id="element-449" />
                  </div>
                  <h3 className="text-xl font-semibold text-white mb-3" data-id="element-450">
                    {benefit.title}
                  </h3>
                  <p className="text-slate-400 leading-relaxed" data-id="element-451">
                    {benefit.description}
                  </p>
                </motion.div>;
          })}
          </motion.div>

          <motion.div initial={{
          opacity: 0,
          y: 20
        }} whileInView={{
          opacity: 1,
          y: 0
        }} viewport={{
          once: true
        }} transition={{
          delay: 0.4
        }} className="text-center mt-12" data-id="element-452">
            <motion.button onClick={() => setFormOpen(true)} className="inline-flex px-8 py-4 bg-gradient-to-r from-orange-500 to-orange-600 text-white font-semibold rounded-full text-lg shadow-lg shadow-orange-500/25 hover:shadow-orange-500/40 transition-all duration-300" whileHover={{
            scale: 1.02,
            y: -2
          }} whileTap={{
            scale: 0.98
          }} data-id="element-453">
              Start Application
            </motion.button>
          </motion.div>
        </div>
      </section>
      <AnimatePresence data-id="element-454">
        {formOpen && <ApplicationForm isOpen={formOpen} onClose={() => setFormOpen(false)} position="technical-assistant" data-id="element-455" />}
      </AnimatePresence>
    </>;
}
function RequirementsSection() {
  const [formOpen, setFormOpen] = useState(false);
  const requirements = [{
    icon: LanguagesIcon,
    title: 'Fluency in English',
    description: 'Navigate the global landscape with ease by being proficient in English, enabling seamless interactions across our diverse team and international clients.'
  }, {
    icon: WrenchIcon,
    title: 'Technical Aptitude',
    description: 'Possess a natural curiosity for technology and ability to learn new systems quickly. Experience with CRM tools, troubleshooting, or technical support is a plus.'
  }, {
    icon: BrainIcon,
    title: 'Problem-Solving Mindset',
    description: 'Demonstrate logical thinking and a systematic approach to solving technical challenges. Ability to break down complex problems into manageable solutions.'
  }, {
    icon: ShieldCheckIcon,
    title: 'Reliability and Precision',
    description: 'Be the dependable force that drives our success. Your commitment to reliability, attention to detail, and precision is the backbone of our efficient team.'
  }, {
    icon: ClockIcon,
    title: 'Full-time Dedication',
    description: 'Devote 40 hours per week in making a difference at CR Careers, contributing your skills and enthusiasm to our collective success.'
  }];
  return <>
      <section className="py-20 sm:py-32 bg-slate-800" data-id="element-456">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8" data-id="element-457">
          <div className="grid lg:grid-cols-2 gap-12 lg:gap-20" data-id="element-458">
            <motion.div initial="hidden" whileInView="visible" viewport={{
            once: true,
            margin: '-100px'
          }} variants={staggerContainer} className="lg:sticky lg:top-32 lg:self-start" data-id="element-459">
              <motion.span variants={fadeInUp} className="text-orange-400 font-semibold text-sm uppercase tracking-wider" data-id="element-460">
                Requirements
              </motion.span>
              <motion.h2 variants={fadeInUp} className="text-3xl sm:text-4xl lg:text-5xl font-bold text-white mt-4 mb-6" data-id="element-461">
                What We Seek in You:
              </motion.h2>
              <motion.p variants={fadeInUp} className="text-slate-400 text-lg" data-id="element-462">
                We're looking for talented individuals who share our passion for
                excellence, customer service, and continuous learning.
              </motion.p>
            </motion.div>

            <motion.div initial="hidden" whileInView="visible" viewport={{
            once: true,
            margin: '-100px'
          }} variants={staggerContainer} className="space-y-4" data-id="element-463">
              {requirements.map((req, index) => <motion.div key={index} variants={fadeInUp} className="flex gap-4 p-5 sm:p-6 rounded-xl bg-slate-900/50 border border-slate-700/50" data-id="element-464">
                  <div className="flex-shrink-0" data-id="element-465">
                    <div className="w-10 h-10 rounded-lg bg-orange-500/10 flex items-center justify-center" data-id="element-466">
                      <req.icon className="w-5 h-5 text-orange-400" data-id="element-467" />
                    </div>
                  </div>
                  <div data-id="element-468">
                    <h3 className="text-lg font-semibold text-white mb-1" data-id="element-469">
                      {req.title}
                    </h3>
                    <p className="text-slate-400 text-sm leading-relaxed" data-id="element-470">
                      {req.description}
                    </p>
                  </div>
                </motion.div>)}
            </motion.div>
          </div>

          <motion.div initial={{
          opacity: 0,
          y: 20
        }} whileInView={{
          opacity: 1,
          y: 0
        }} viewport={{
          once: true
        }} transition={{
          delay: 0.4
        }} className="text-center mt-16" data-id="element-471">
            <motion.button onClick={() => setFormOpen(true)} className="inline-flex px-8 py-4 bg-gradient-to-r from-orange-500 to-orange-600 text-white font-semibold rounded-full text-lg shadow-lg shadow-orange-500/25 hover:shadow-orange-500/40 transition-all duration-300" whileHover={{
            scale: 1.02,
            y: -2
          }} whileTap={{
            scale: 0.98
          }} data-id="element-472">
              Start Application
            </motion.button>
          </motion.div>
        </div>
      </section>
      <AnimatePresence data-id="element-473">
        {formOpen && <ApplicationForm isOpen={formOpen} onClose={() => setFormOpen(false)} position="technical-assistant" data-id="element-474" />}
      </AnimatePresence>
    </>;
}
function CTASection() {
  const [formOpen, setFormOpen] = useState(false);
  return <>
      <section id="apply" className="py-20 sm:py-32 bg-gradient-to-b from-slate-900 to-slate-950 relative overflow-hidden" data-id="element-475">
        <div className="absolute inset-0 overflow-hidden" data-id="element-476">
          <div className="absolute top-1/2 left-1/2 -translate-x-1/2 -translate-y-1/2 w-[600px] h-[600px] bg-orange-500/10 rounded-full blur-3xl" data-id="element-477" />
        </div>

        <div className="relative z-10 max-w-4xl mx-auto px-4 sm:px-6 lg:px-8 text-center" data-id="element-478">
          <motion.div initial="hidden" whileInView="visible" viewport={{
          once: true,
          margin: '-100px'
        }} variants={staggerContainer} className="space-y-8" data-id="element-479">
            <motion.h2 variants={fadeInUp} className="text-3xl sm:text-4xl lg:text-5xl font-bold text-white" data-id="element-480">
              Join us at CR Careers
            </motion.h2>

            <motion.p variants={fadeInUp} className="text-lg sm:text-xl text-slate-400 max-w-2xl mx-auto" data-id="element-481">
              Where your career journey aligns with innovation, flexibility, and
              limitless possibilities. Apply now and be part of a team that's
              shaping the future!
            </motion.p>

            <motion.div variants={fadeInUp} data-id="element-482">
              <motion.button onClick={() => setFormOpen(true)} className="inline-flex px-10 py-5 bg-gradient-to-r from-orange-500 to-orange-600 text-white font-bold rounded-full text-xl shadow-xl shadow-orange-500/30 hover:shadow-orange-500/50 transition-all duration-300" whileHover={{
              scale: 1.05,
              y: -3
            }} whileTap={{
              scale: 0.98
            }} data-id="element-483">
                Start Application
              </motion.button>
            </motion.div>
          </motion.div>
        </div>
      </section>
      <AnimatePresence data-id="element-484">
        {formOpen && <ApplicationForm isOpen={formOpen} onClose={() => setFormOpen(false)} position="technical-assistant" data-id="element-485" />}
      </AnimatePresence>
    </>;
}
function Footer() {
  const [showImpressum, setShowImpressum] = useState(false);
  const [showDatenschutz, setShowDatenschutz] = useState(false);
  return <>
      <footer className="bg-slate-950 border-t border-slate-800" data-id="element-486">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-12" data-id="element-487">
          <div className="text-center space-y-6" data-id="element-488">
            <div className="text-white font-bold text-2xl mb-4" data-id="element-489">CR Careers</div>

            <div className="flex justify-center gap-6 text-sm text-slate-400" data-id="element-490">
              <button onClick={() => setShowImpressum(true)} className="hover:text-white transition-colors" data-id="element-491">
                Impressum
              </button>
              <span className="text-slate-700" data-id="element-492">|</span>
              <button onClick={() => setShowDatenschutz(true)} className="hover:text-white transition-colors" data-id="element-493">
                Datenschutz
              </button>
            </div>

            <p className="text-slate-400 text-sm" data-id="element-494">
              © {new Date().getFullYear()} CR Careers. All rights reserved.
            </p>
            <p className="text-slate-500 text-xs max-w-3xl mx-auto leading-relaxed" data-id="element-495">
              NOT GOOGLE: This site is not a part of the Google website or
              Google Inc. Additionally, This site is NOT endorsed by Google in
              any way. This site is not a part of the Facebook website or
              Facebook Inc. Additionally, This site is NOT endorsed by Facebook
              in any way. FACEBOOK is a trademark of FACEBOOK, Inc.
            </p>
          </div>
        </div>
      </footer>
      <AnimatePresence data-id="element-496">
        {showImpressum && <ImpressumModal isOpen={showImpressum} onClose={() => setShowImpressum(false)} data-id="element-497" />}
        {showDatenschutz && <DatenschutzModal isOpen={showDatenschutz} onClose={() => setShowDatenschutz(false)} data-id="element-498" />}
      </AnimatePresence>
    </>;
}
export function TechnicalAssistantPage() {
  return <div className="min-h-screen w-full bg-slate-900 overflow-x-hidden" data-id="element-499">
      <Header data-id="element-500" />
      <main className="overflow-x-hidden" data-id="element-501">
        <HeroSection data-id="element-502" />
        <AboutSection data-id="element-503" />
        <BenefitsSection data-id="element-504" />
        <RequirementsSection data-id="element-505" />
        <CTASection data-id="element-506" />
      </main>
      <Footer data-id="element-507" />
    </div>;
}